import React, { useEffect, useState } from "react";
import moment from "moment";
import styled from "styled-components";
import { FormControl } from "react-bootstrap";
import {
    DEFAULT_BASIC_CONFIG,
    Field,
    FieldConfig,
    FieldDataTypeEnum,
    FieldType,
    FormFieldTest,
} from "../../../types/types";
import AMSSelect from "../../AMSSelect";
import { RadioItem } from "../FieldSettings/RadioItem";
import { CheckboxItem } from "../../listing/ColumnIdentifier";
import FileUpload from "../../form/ProfileImageUpload";
import CustomInput, { ErrorText } from "../../form/CustomInput";
import CustomTextArea from "../../form/CustomTextArea";
import CalendarIcon from "../../../assets/icons/CalendarIcon";
import ClockIcon from "../../../assets/icons/ClockIcon";
import { useTheme } from "../../../middleware/ThemeContext";
import { showToastOutsideReact } from "../../toast/ToastContext";
import { loadGoogleMapsScript } from "../../../utils/googleMapsLoader";
import { PhoneErrorCode, PhoneValidationOptions, validatePhoneNumber } from "../../../utils/phoneValidation";

export interface FieldControlProps {
    field: Field | null | undefined;
    isRequired?: boolean;
    value: any;
    onChange: (value: any) => void;
    onBlur?: () => void;
    onValidationChange?: (error: string | null) => void;
    error?: string;
    uploadFolder?: string;
    forPreview?: boolean;
    phoneValidationOptions?: PhoneValidationOptions;
}

const DATE_INPUT_FORMATS = [
    "MM-DD-YYYY",
    "MM/DD/YYYY",
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "MM.DD.YYYY",
    "MMM D, YYYY",
    "MMMM D, YYYY",
    "MM-DD-YY",
    "MM/DD/YY",
    "YYYY-MM",
];

const parseDateLikeValue = (raw: any): moment.Moment | null => {
    if (raw === undefined || raw === null || raw === "") {
        return null;
    }

    if (moment.isMoment(raw)) {
        return raw as moment.Moment;
    }

    if (raw instanceof Date && !isNaN(raw.getTime())) {
        return moment(raw);
    }

    if (typeof raw === "number") {
        const parsed = moment(raw);
        return parsed.isValid() ? parsed : null;
    }

    if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed) return null;
        let parsed = moment(trimmed, DATE_INPUT_FORMATS, true);
        if (!parsed.isValid()) {
            parsed = moment(trimmed);
        }
        return parsed.isValid() ? parsed : null;
    }

    if (typeof raw === "object") {
        const candidate = (raw as any)?.value || (raw as any)?.formattedValue || (raw as any)?.formattedAddress;
        return candidate ? parseDateLikeValue(candidate) : null;
    }

    return null;
};

const formatDateValueForInput = (
    raw: any,
    options: { includeTime?: boolean; isMonthYear?: boolean } = {}
): string => {
    const parsed = parseDateLikeValue(raw);
    if (!parsed) {
        return "";
    }

    if (options.isMonthYear) {
        return parsed.format("YYYY-MM");
    }

    if (options.includeTime) {
        return parsed.format("YYYY-MM-DDTHH:mm");
    }

    return parsed.format("YYYY-MM-DD");
};

const validateField = (
    value: any,
    config: FieldConfig,
    fieldDataType: string,
    isRequired: boolean
): string | null => {
    if (isRequired) {
        if (value === undefined || value === null || value === "") {
            const labelForMsg = config.uiMeta.displayName || config.uiMeta.fieldLabel || "This field";
            return `${labelForMsg} is required`;
        }
        if (Array.isArray(value) && value.length === 0) {
            const labelForMsg = config.uiMeta.displayName || config.uiMeta.fieldLabel || "This field";
            return `${labelForMsg} is required`;
        }
    }

    switch (fieldDataType) {
        case "email":
            if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email format";
            break;
        case "website":
            if (value && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/.test(value)) return "Invalid website URL";
            break;
        case "NUMBERS":
        case "currency": {
            if (value && isNaN(Number(value))) return "Must be a number";
            const allowDec = !!config?.uiMeta?.allowDecimal || fieldDataType === "currency";
            if (value) {
                const str = String(value);
                const hasDot = str.includes(".") || str.includes(",");
                if (!allowDec && hasDot) return "Decimals are not allowed";
                if (allowDec && config?.uiMeta?.decimalPlaces !== undefined) {
                    const parts = str.split(".");
                    const decimals = parts[1]?.length || 0;
                    if (decimals > (config?.uiMeta?.decimalPlaces || 4)) return `Maximum ${config?.uiMeta?.decimalPlaces || 4} decimal places allowed`;
                }
            }
            if (config?.uiMeta.rangeEnabled && value) {
                const numValue = Number(value);
                if (config.uiMeta.minRange !== undefined && numValue < Number(config.uiMeta.minRange)) return `Minimum value is ${config.uiMeta.minRange}`;
                if (config.uiMeta.maxRange !== undefined && numValue > Number(config.uiMeta.maxRange)) return `Maximum value is ${config.uiMeta.maxRange}`;
            }
            break;
        }
        case "date":
        case "time":
        case "datetime-local":
        case "month":
            if (value && config?.uiMeta.allowedDates) {
                if (config.uiMeta.allowedDates.min && value < config.uiMeta.allowedDates.min) return `Must be after ${config.uiMeta.allowedDates.min}`;
                if (config.uiMeta.allowedDates.max && value > config.uiMeta.allowedDates.max) return `Must be before ${config.uiMeta.allowedDates.max}`;
            }
            break;
        case "single-line":
        case "multi-line":
            if (config?.uiMeta.characterLimitEnabled && value?.length > (config.uiMeta.maxLength || Infinity)) return `Exceeds ${config.uiMeta.maxLength} character limit`;
            if (config?.uiMeta.characterLimitEnabled && config.uiMeta.minEnabled && value?.length < (config.uiMeta.minValue || 0)) return `Minimum ${config.uiMeta.minValue} characters required`;
            break;
    }
    return null;
};

const isOtherValue = (v: any): boolean => typeof v === "string" && (v === "Other" || v.startsWith("Other:"));
const getOtherText = (v: any): string => (typeof v === "string" && v.startsWith("Other:")) ? v.substring("Other:".length).trim() : "";

export const FieldControl: React.FC<FieldControlProps> = ({
    field,
    isRequired,
    value,
    onChange,
    onBlur,
    onValidationChange,
    error,
    uploadFolder,
    forPreview,
    phoneValidationOptions,
}) => {
    const normalizeCheckboxChecked = (v: any): boolean => {
        if (v === true) return true;
        if (v === false || v === undefined || v === null) return false;

        if (typeof v === 'number') {
            return v === 1;
        }

        if (typeof v === 'string') {
            const s = v.trim().toLowerCase();
            if (!s) return false;
            return s === 'true' || s === '1' || s === 'yes' || s === 'y' || s === 'on';
        }

        return Boolean(v);
    };

    const { theme } = useTheme();
    const config = (field?.config as FieldConfig) || DEFAULT_BASIC_CONFIG;
    const fieldDataType = field?.dataType as FieldDataTypeEnum;
    const fieldType = field?.fieldType as FieldType;
    const normalizedFieldType = String(fieldType || '').toLowerCase();
    const includeOther = !!config?.uiMeta?.includeOtherOption;

    const combinedFieldText = React.useMemo(() => {
        return [
            field?.name,
            field?.label,
            config?.uiMeta?.displayName,
            config?.uiMeta?.fieldLabel,
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
    }, [field?.name, field?.label, config?.uiMeta?.displayName, config?.uiMeta?.fieldLabel]);

    const normalizedDataType = typeof fieldDataType === 'string' ? fieldDataType.toLowerCase() : '';
    const containsPhoneKeyword = combinedFieldText.includes('phone');
    const looksLikeCountryField = /\bcountry\b/.test(combinedFieldText) || combinedFieldText.includes('country code');
    const looksLikeExtensionField = /\b(extension|ext)\b/.test(combinedFieldText);
    const isPhoneFieldType = fieldType === 'PHONE_NO' || fieldType === 'MOBILE_NO';
    const isTextInputField = fieldDataType === FieldDataTypeEnum.SINGLE_LINE || normalizedDataType === 'phone-no';
    const isPhoneCandidate = isPhoneFieldType || normalizedDataType.includes('phone') || containsPhoneKeyword;
    const isPhoneNumberField = isPhoneCandidate && isTextInputField && containsPhoneKeyword && !looksLikeCountryField && !looksLikeExtensionField;
    const isPhoneExtensionField = isPhoneCandidate && isTextInputField && looksLikeExtensionField;

    const phoneFieldLabel = config?.uiMeta?.displayName || config?.uiMeta?.fieldLabel || field?.label || field?.name || 'Phone number';
    const displayOnlyMode = normalizedFieldType === 'paragraph'
        ? 'paragraph'
        : normalizedFieldType === 'divider'
            ? 'divider'
            : 'heading';
    const displayOnlyHeadingLevel = (config?.uiMeta?.headingLevel || 'H1').toUpperCase();
    const displayOnlyHeadingSizeMap: Record<string, string> = {
        H1: '48px',
        H2: '40px',
        H3: '32px',
        H4: '24px',
        H5: '20px',
        H6: '16px',
    };
    const displayOnlyHeadingTag = (displayOnlyHeadingLevel.toLowerCase() as keyof React.JSX.IntrinsicElements) || 'h1';
    const displayOnlyHeadingFontSize = config?.uiMeta?.fontSize || displayOnlyHeadingSizeMap[displayOnlyHeadingLevel] || '48px';
    const displayOnlyHeadingWeight = config?.uiMeta?.fontWeight || 'bold';
    const displayOnlyHeadingColor = config?.uiMeta?.color || '#3B6FF5';
    const displayOnlyHeadingAlign = config?.uiMeta?.textAlignment || 'left';
    const decodeHtmlEntities = (html: string) => {
        if (!html) return '';

        try {
            const textarea = document.createElement('textarea');
            textarea.innerHTML = html;
            return textarea.value;
        } catch {
            return html;
        }
    };

    const normalizeQuillAlignmentHtml = (html: string) => {
        if (!html) return '';
        if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return html;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        doc.querySelectorAll("[class*='ql-align-']").forEach((node) => {
            const element = node as HTMLElement;
            const alignClass = Array.from(element.classList).find((className) => className.startsWith('ql-align-'));
            if (!alignClass) return;

            const alignment = alignClass.replace('ql-align-', '');
            if (!['left', 'center', 'right', 'justify'].includes(alignment)) return;

            element.style.textAlign = alignment;
            element.classList.remove(alignClass);
        });

        return doc.body.innerHTML;
    };

    const displayOnlyRawText = config?.uiMeta?.htmlContent
        || config?.uiMeta?.paragraphContent
        || config?.uiMeta?.displayValue
        || 'Let\'s Create Something Amazing Together';
    const displayOnlyText = normalizeQuillAlignmentHtml(decodeHtmlEntities(displayOnlyRawText.trim()));
    const displayOnlyParagraphIsHtml = /<[^>]*>/.test(displayOnlyText);
    const displayOnlyParagraphFontSize = config?.uiMeta?.paragraphFontSize || '18px';
    const displayOnlyParagraphWeight = config?.uiMeta?.paragraphFontWeight || 'regular';
    const displayOnlyParagraphColor = config?.uiMeta?.paragraphColor || '#3B6FF5';
    const displayOnlyParagraphAlign = config?.uiMeta?.paragraphTextAlignment || 'left';
    const displayOnlyParagraphLineHeight = config?.uiMeta?.paragraphLineHeight || '1.6';
    const displayOnlyDividerColor = config?.uiMeta?.separatorColor || '#BABDC1';
    const displayOnlyDividerHeight = config?.uiMeta?.separatorHeight || '2px';
    const displayOnlyDividerStyle = config?.uiMeta?.separatorStyle || 'solid';
    const displayOnlyDividerWidth = config?.uiMeta?.separatorWidth || '100%';
    const displayOnlyDividerSpacing = config?.uiMeta?.separatorSpacing || '16px 0';

    const normalizedValue = React.useMemo(() => {
        if (fieldDataType === FieldDataTypeEnum.DATE) {
            return formatDateValueForInput(value);
        }
        if (fieldDataType === FieldDataTypeEnum.DATE_TIME) {
            return formatDateValueForInput(value, { includeTime: !!config?.uiMeta?.includeTimeInDate });
        }
        if (fieldDataType === FieldDataTypeEnum.MONTH_YEAR) {
            return formatDateValueForInput(value, { isMonthYear: true });
        }
        return value || "";
    }, [value, fieldDataType, config?.uiMeta?.includeTimeInDate]);

    // Normalize options to simple string values to support both string[] and {label,value}[]
    const getOptionValues = React.useCallback((): string[] => {
        const raw: any[] = Array.isArray(config?.options) ? (config?.options as any[]) : [];
        return raw.map((opt) => {
            if (typeof opt === 'string') return opt;
            if (opt && typeof opt === 'object') {
                return String(opt.value ?? opt.label ?? '');
            }
            return '';
        }).filter(Boolean);
    }, [config?.options]);

    const choiceSortOrder = config?.uiMeta?.choiceSortOrder as 'asc' | 'desc' | undefined;

    const selectOptions = React.useMemo(() => {
        const optionValues = getOptionValues();
        const withOther = includeOther && !optionValues.includes('Other')
            ? [...optionValues, 'Other']
            : [...optionValues];

        const mapped = withOther.map((choice) => ({ label: choice, value: choice }));

        if (!choiceSortOrder) {
            return mapped;
        }

        const sorted = [...mapped].sort((a, b) => {
            const aOther = a.label === 'Other';
            const bOther = b.label === 'Other';
            if (aOther || bOther) {
                if (aOther && bOther) return 0;
                return aOther ? 1 : -1;
            }
            return choiceSortOrder === 'asc'
                ? a.label.localeCompare(b.label)
                : b.label.localeCompare(a.label);
        });

        return sorted;
    }, [getOptionValues, includeOther, choiceSortOrder]);

    const multiChoiceValue = React.useMemo(() => {
        if (!Array.isArray(value)) return [];
        return value.map((val) => {
            const normalized = includeOther && isOtherValue(val) ? 'Other' : String(val ?? '');
            return selectOptions.find((opt) => opt.value === normalized) ?? { label: normalized, value: normalized };
        });
    }, [value, includeOther, selectOptions]);

    const singleChoiceValue = React.useMemo(() => {
        if (value === undefined || value === null || Array.isArray(value)) return null;
        const rawValue = typeof value === 'string' ? value : String(value);
        if (!rawValue) return null;
        const normalized = includeOther && isOtherValue(rawValue) ? 'Other' : rawValue;
        return selectOptions.find((opt) => opt.value === normalized) ?? { label: normalized, value: normalized };
    }, [value, includeOther, selectOptions]);

    const [showOtherSingle, setShowOtherSingle] = useState<boolean>(false);
    const [otherSingleText, setOtherSingleText] = useState<string>("");
    const [showOtherMulti, setShowOtherMulti] = useState<boolean>(false);
    const [otherMultiText, setOtherMultiText] = useState<string>("");

    const handleValidation = (val: any) => {
        if (!onValidationChange) return;
        let validationError = validateField(val, config, fieldDataType, isRequired || false);

        if (!validationError && isPhoneNumberField) {
            const rawInput = typeof val === 'string' ? val : val ? String(val) : '';
            if (rawInput.trim()) {
                const phoneResult = validatePhoneNumber(rawInput, phoneValidationOptions);
                if (!phoneResult.isValid) {
                    if (phoneResult.errorCode === PhoneErrorCode.PHONE_COUNTRY_REQUIRED && phoneValidationOptions?.countryFieldLabel) {
                        validationError = `${phoneValidationOptions.countryFieldLabel} is required when ${phoneFieldLabel} is provided`;
                    } else {
                        validationError = phoneResult.message || 'Invalid phone number';
                    }
                }
            }
        }

        if (!validationError && isPhoneExtensionField && val) {
            const extensionValue = typeof val === 'string' ? val.trim() : String(val).trim();
            if (extensionValue && !/^[0-9]+$/.test(extensionValue)) {
                validationError = 'Extension must contain digits only';
            }
        }

        if (!validationError && includeOther) {
            const opts = config?.options || [];
            if (
                (fieldDataType === FieldDataTypeEnum.SELECT || fieldDataType === FieldDataTypeEnum.RADIO)
                && val === 'Other'
                && (!otherSingleText || otherSingleText.trim() === '')
            ) {
                validationError = 'Specify Other please as you have selected other option';
            }

            if (
                !validationError && fieldDataType === FieldDataTypeEnum.MULTIPLE_CHOICES
                && Array.isArray(val)
                && val.includes('Other')
            ) {
                const hasCustom = val.some(v => typeof v === 'string' && v !== 'Other' && !opts.includes(v));
                if (!hasCustom && (!otherMultiText || otherMultiText.trim() === '')) {
                    validationError = 'Specify Other please as you have selected other option';
                }
            }
        }

        onValidationChange?.(validationError);
    };

    const handleChange = (newValue: any) => {
        onChange(newValue);
        handleValidation(newValue);
    };

    useEffect(() => {
        handleValidation(value);
    }, [
        value,
        config,
        showOtherSingle,
        showOtherMulti,
        phoneValidationOptions?.countryCallingCode,
        phoneValidationOptions?.countryCode,
        phoneValidationOptions?.allowSmsOnly,
        phoneValidationOptions?.countryFieldLabel,
        isPhoneNumberField,
        isPhoneExtensionField,
    ]);

    useEffect(() => {
        if (fieldDataType !== FieldDataTypeEnum.MULTIPLE_CHOICES) return;
        const opts = getOptionValues();
        const def = config?.uiMeta?.defaultSelection;
        const validDefault = def && def !== 'Other' && opts.includes(def) ? def : undefined;
        const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
        if (!hasValue && validDefault) {
            onChange([validDefault]);
        }
    }, [fieldDataType, config?.uiMeta?.defaultSelection, getOptionValues, field?.id]);

    useEffect(() => {
        const opts = getOptionValues();
        if (fieldDataType === FieldDataTypeEnum.SELECT || fieldDataType === FieldDataTypeEnum.RADIO) {
            const otherSelected = includeOther && isOtherValue(value);
            setShowOtherSingle(!!otherSelected);
            if (otherSelected) setOtherSingleText(getOtherText(value));
            else {
                const isCustom = typeof value === 'string' && value && value !== 'Other' && !opts.includes(value);
                if (isCustom) setOtherSingleText(value as string);
            }
        }
        if (fieldDataType === FieldDataTypeEnum.MULTIPLE_CHOICES) {
            if (Array.isArray(value)) {
                const otherEntry = value.find(v => includeOther && isOtherValue(v));
                const custom = value.find(v => typeof v === 'string' && !opts.includes(v) && !isOtherValue(v));
                setShowOtherMulti(!!otherEntry || !!custom);
                if (otherEntry) setOtherMultiText(getOtherText(otherEntry));
                else if (custom) setOtherMultiText(custom as string);
            } else {
                setShowOtherMulti(false);
            }
        }
    }, [value, fieldDataType, getOptionValues, includeOther]);

    useEffect(() => {
        if (fieldDataType !== FieldDataTypeEnum.MULTIPLE_CHOICES) return;
        const opts = getOptionValues();
        const current = Array.isArray(value) ? value : [];
        const sanitized = current.filter(v => {
            if (typeof v !== 'string') return false;
            if (isOtherValue(v)) return includeOther;
            return opts.includes(v);
        });
        const changed = sanitized.length !== current.length || sanitized.some((v, i) => v !== current[i]);
        if (changed) {
            onChange(sanitized);
            if (!sanitized.includes('Other')) {
                setShowOtherMulti(false);
                setOtherMultiText('');
            }
        }
    }, [fieldDataType, getOptionValues, includeOther]);

    useEffect(() => {
        if (!includeOther && (fieldDataType === FieldDataTypeEnum.SELECT || fieldDataType === FieldDataTypeEnum.RADIO)) {
            if (isOtherValue(value)) {
                onChange('');
                setShowOtherSingle(false);
                setOtherSingleText('');
            }
        }
    }, [includeOther, fieldDataType]);

    const getCommonProps = () => ({
        placeholder: config?.uiMeta?.placeholder || field?.label ? `Enter ${field?.label} here` : field?.name ? `Enter ${field?.name} here` : 'Enter Value here',
        value: normalizedValue,
        onChange: handleChange,
        onBlur,
        required: isRequired || false,
        disabled: config?.uiMeta?.disabled,
        error: error,
        forPreview: forPreview,
    });

    const getNumericInputProps = () => ({
        ...getCommonProps(),
        min: config?.uiMeta?.rangeEnabled ? config?.uiMeta?.minRange : undefined,
        max: config?.uiMeta?.rangeEnabled ? config?.uiMeta?.maxRange : undefined,
        step: (() => {
            const allowDec = !!config?.uiMeta?.allowDecimal;
            const dp = config?.uiMeta?.decimalPlaces;
            if (!allowDec) return '1';
            if (dp === undefined) return 'any';
            if (dp === 0) return '1';
            return '0.' + '0'.repeat(Math.max(0, dp - 1)) + '1';
        })(),
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
            const allowDec = !!config?.uiMeta?.allowDecimal;
            if (!allowDec && (e.key === '.' || e.key === ',')) {
                e.preventDefault();
            }
        },
        onChange: (raw: any) => {
            const allowDec = !!config?.uiMeta?.allowDecimal;
            const dp = config?.uiMeta?.decimalPlaces || 4;
            let s = String(raw ?? '');
            s = s.replace(/,/g, '.');
            const isNeg = s.startsWith('-');
            let digits = s.replace(/[^0-9.]/g, '');
            if (!allowDec) {
                digits = digits.replace(/\./g, '');
            } else {
                const firstDot = digits.indexOf('.');
                if (firstDot !== -1) {
                    const before = digits.slice(0, firstDot + 1);
                    const afterRaw = digits.slice(firstDot + 1).replace(/\./g, '');
                    const after = typeof dp === 'number' ? afterRaw.slice(0, Math.max(0, dp)) : afterRaw;
                    digits = before + after;
                }
            }
            if (isNeg) {
                digits = digits ? '-' + digits : '-';
            }
            if (digits === '-') handleChange(digits); else handleChange(digits);
        },
    });

    const renderSwitch = () => {
        switch (fieldDataType) {
            case FieldDataTypeEnum.DISPLAY_ONLY:
                if (displayOnlyMode === 'divider') {
                    return (
                        <DisplayOnlyDivider
                            $color={displayOnlyDividerColor}
                            $height={displayOnlyDividerHeight}
                            $style={displayOnlyDividerStyle}
                            $width={displayOnlyDividerWidth}
                            $spacing={displayOnlyDividerSpacing}
                        />
                    );
                }

                if (displayOnlyMode === 'paragraph') {
                    return (
                        <DisplayOnlyParagraph
                            className="ql-editor"
                            $color={displayOnlyParagraphColor}
                            $fontSize={displayOnlyParagraphFontSize}
                            $fontWeight={displayOnlyParagraphWeight}
                            $lineHeight={displayOnlyParagraphLineHeight}
                            $preview={forPreview}
                            style={displayOnlyParagraphIsHtml ? undefined : { textAlign: displayOnlyParagraphAlign }}
                            dangerouslySetInnerHTML={displayOnlyParagraphIsHtml ? { __html: displayOnlyText } : undefined}
                        >
                            {!displayOnlyParagraphIsHtml ? displayOnlyText : null}
                        </DisplayOnlyParagraph>
                    );
                }

                return (
                    <DisplayOnlyHeading
                        as={displayOnlyHeadingTag}
                        $align={displayOnlyHeadingAlign}
                        $color={displayOnlyHeadingColor}
                        $fontSize={displayOnlyHeadingFontSize}
                        $fontWeight={displayOnlyHeadingWeight}
                    >
                        {displayOnlyText}
                    </DisplayOnlyHeading>
                );

            case FieldDataTypeEnum.SINGLE_LINE:
                if (fieldType === 'ADDRESS') {
                    return (
                        <AddressAutocomplete
                            value={value || ""}
                            onChange={handleChange}
                            onAutoFill={(payload) => {
                                try {
                                    const name = (field?.name || '').toLowerCase();
                                    const scope = name.includes('home')
                                        ? 'home'
                                        : name.includes('work')
                                            ? 'work'
                                            : name.includes('other')
                                                ? 'other'
                                                : 'unknown';
                                    const detail = {
                                        scope,
                                        sourceFieldId: field?.id,
                                        formattedAddress: payload.formattedAddress,
                                        value: payload.value,
                                        components: payload.components,
                                        location: payload.location,
                                    };
                                    window.dispatchEvent(
                                        new CustomEvent('AMS_ADDRESS_AUTOFILL', { detail })
                                    );
                                } catch { }
                            }}
                            placeholder={config?.uiMeta?.placeholder || "Enter address"}
                            disabled={config?.uiMeta?.disabled}
                            mode="address-line"
                        />
                    );
                }
                if (fieldType === 'CITY') {
                    return (
                        <AddressAutocomplete
                            value={value || ""}
                            onChange={handleChange}
                            onAutoFill={(payload) => {
                                try {
                                    const name = (field?.name || '').toLowerCase();
                                    const scope = name.includes('home')
                                        ? 'home'
                                        : name.includes('work')
                                            ? 'work'
                                            : name.includes('other')
                                                ? 'other'
                                                : 'unknown';
                                    const detail = {
                                        scope,
                                        sourceFieldId: field?.id,
                                        formattedAddress: payload.formattedAddress,
                                        value: payload.value,
                                        components: payload.components,
                                        location: payload.location,
                                    };
                                    window.dispatchEvent(
                                        new CustomEvent('AMS_ADDRESS_AUTOFILL', { detail })
                                    );
                                } catch { }
                            }}
                            placeholder={config?.uiMeta?.placeholder || "Enter city"}
                            disabled={config?.uiMeta?.disabled}
                            mode="city"
                        />
                    );
                }
                if (fieldType === 'STATE') {
                    return (
                        <AddressAutocomplete
                            value={value || ""}
                            onChange={handleChange}
                            onAutoFill={(payload) => {
                                try {
                                    const name = (field?.name || '').toLowerCase();
                                    const scope = name.includes('home')
                                        ? 'home'
                                        : name.includes('work')
                                            ? 'work'
                                            : name.includes('other')
                                                ? 'other'
                                                : 'unknown';
                                    const detail = {
                                        scope,
                                        sourceFieldId: field?.id,
                                        formattedAddress: payload.formattedAddress,
                                        value: payload.value,
                                        components: payload.components,
                                        location: payload.location,
                                    };
                                    window.dispatchEvent(
                                        new CustomEvent('AMS_ADDRESS_AUTOFILL', { detail })
                                    );
                                } catch { }
                            }}
                            placeholder={config?.uiMeta?.placeholder || "Enter state"}
                            disabled={config?.uiMeta?.disabled}
                            mode="state"
                        />
                    );
                }
                if (fieldType === 'ZIP_CODE') {
                    return (
                        <AddressAutocomplete
                            value={value || ""}
                            onChange={handleChange}
                            onAutoFill={(payload) => {
                                try {
                                    const name = (field?.name || '').toLowerCase();
                                    const scope = name.includes('home')
                                        ? 'home'
                                        : name.includes('work')
                                            ? 'work'
                                            : name.includes('other')
                                                ? 'other'
                                                : 'unknown';
                                    const detail = {
                                        scope,
                                        sourceFieldId: field?.id,
                                        formattedAddress: payload.formattedAddress,
                                        value: payload.value,
                                        components: payload.components,
                                        location: payload.location,
                                    };
                                    window.dispatchEvent(
                                        new CustomEvent('AMS_ADDRESS_AUTOFILL', { detail })
                                    );
                                } catch { }
                            }}
                            placeholder={config?.uiMeta?.placeholder || "Enter zip/postal code"}
                            disabled={config?.uiMeta?.disabled}
                            mode="postal-code"
                        />
                    );
                }
                return <CustomInput {...getCommonProps()} />;

            case FieldDataTypeEnum.MULTI_LINE:
                return (
                    <CustomTextArea
                        {...getCommonProps()}
                        minLength={config?.uiMeta?.characterLimitEnabled && config?.uiMeta?.minEnabled ? config?.uiMeta?.minValue : undefined}
                        maxLength={config?.uiMeta?.characterLimitEnabled ? config?.uiMeta?.maxLength : undefined}
                    />
                );

            case FieldDataTypeEnum.NUMBERS:
                return <CustomInput {...getNumericInputProps()} type="number" />;

            case FieldDataTypeEnum.RADIO: {
                const opts = [...getOptionValues()];
                if (includeOther && !opts.includes('Other')) opts.push('Other');
                return (
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {opts.map((choice, index) => (
                            <RadioItem theme={theme} key={index}>
                                <input
                                    type="radio"
                                    name={`radio-${field?.id}`}
                                    value={choice}
                                    checked={(isOtherValue(value) ? 'Other' : value) === choice}
                                    onChange={() => {
                                        if (includeOther && choice === 'Other') {
                                            setShowOtherSingle(true);
                                            handleChange('Other');
                                            setOtherSingleText('');
                                        } else {
                                            setShowOtherSingle(false);
                                            handleChange(choice);
                                        }
                                    }}
                                    disabled={config?.uiMeta?.disabled}
                                    required={isRequired || false}
                                />
                                <span>{choice}</span>
                            </RadioItem>
                        ))}
                        {includeOther && showOtherSingle && (
                            <div style={{ marginTop: 8 }}>
                                <SmallLabel theme={theme}>Please specify</SmallLabel>
                                <TextInput
                                    theme={theme}
                                    type="text"
                                    placeholder="Specify Other option"
                                    value={otherSingleText}
                                    onChange={(e) => setOtherSingleText(e.target.value)}
                                    onBlur={() => {
                                        const txt = otherSingleText;
                                        handleChange(txt.trim().length > 0 ? `Other: ${txt}` : 'Other');
                                        setShowOtherSingle(isOtherValue(value));
                                        handleValidation(value);
                                    }}
                                    disabled={config?.uiMeta?.disabled}
                                />
                                {error && <ErrorText>{error}</ErrorText>}
                            </div>
                        )}
                    </div>
                );
            }

            case FieldDataTypeEnum.CHECKBOX:
                return (
                    <div style={{ width: '100%', overflow: 'hidden' }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px", width: '100%' }}>
                            <CheckboxItem theme={theme} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '8px',
                                width: '100%',
                                height: '42px',
                                maxWidth: '100%',
                                overflow: 'hidden'
                            }}>
                                <input
                                    id={`checkbox-${field?.id ?? field?.name ?? 'field'}`}
                                    type="checkbox"
                                    checked={normalizeCheckboxChecked(value)}
                                    onChange={(e) => handleChange(e.target.checked)}
                                    style={{ marginTop: '2px', flexShrink: 0 }}
                                    disabled={config?.uiMeta?.disabled}
                                    required={isRequired || false}
                                />
                                <label
                                    htmlFor={`checkbox-${field?.id ?? field?.name ?? 'field'}`}
                                    style={{
                                    flex: 1,
                                    minWidth: 0,
                                    wordBreak: 'break-all',
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal',
                                    lineHeight: '1.4',
                                    overflow: 'hidden'
                                }}>
                                    {field?.config?.uiMeta?.fieldLabel}
                                </label>
                            </CheckboxItem>
                        </div>
                        {error && <ErrorText>{error}</ErrorText>}
                    </div>
                );

            case FieldDataTypeEnum.MULTIPLE_CHOICES:
                return (
                    <div>
                        <AMSSelect
                            staticOptions={selectOptions}
                            value={multiChoiceValue}
                            onChange={(selectedValue: any) => {
                                const selectedArray = Array.isArray(selectedValue)
                                    ? selectedValue
                                    : selectedValue
                                        ? [selectedValue]
                                        : [];
                                const normalizedSelections = selectedArray
                                    .map((opt) => String(opt?.value ?? opt?.label ?? ''))
                                    .filter(Boolean);

                                const prevArray = Array.isArray(value) ? (value as string[]) : [];
                                const hadPrevOther = prevArray.find((v) => isOtherValue(v));
                                const existingText = otherMultiText || (hadPrevOther ? getOtherText(hadPrevOther) : '');

                                let nextValues = normalizedSelections.filter((val) => val !== 'Other');
                                if (normalizedSelections.includes('Other')) {
                                    if (existingText && existingText.trim()) {
                                        nextValues = [...nextValues, `Other: ${existingText.trim()}`];
                                    } else {
                                        nextValues = [...nextValues, 'Other'];
                                    }
                                }

                                handleChange(nextValues);
                                if (includeOther) {
                                    const hasOther = nextValues.some((entry) => isOtherValue(entry));
                                    if (!hasOther) {
                                        setOtherMultiText('');
                                    }
                                    setShowOtherMulti(hasOther);
                                }
                            }}
                            placeholder={config?.uiMeta?.placeholder || "Select options here"}
                            isDisabled={config?.uiMeta?.disabled}
                            isSearchable
                            isMulti
                            defaultOptions
                        />
                        {includeOther && showOtherMulti && (
                            <div style={{ marginTop: 8 }}>
                                <SmallLabel theme={theme}>Please specify</SmallLabel>
                                <TextInput
                                    theme={theme}
                                    type="text"
                                    placeholder="Specify Other..."
                                    value={otherMultiText}
                                    onChange={(e) => setOtherMultiText(e.target.value)}
                                    onBlur={() => {
                                        const txt = otherMultiText;
                                        const current = Array.isArray(value) ? (value as string[]) : [];
                                        const hasOther = current.some(v => isOtherValue(v));
                                        if (hasOther) {
                                            const withoutOther = current.filter(v => !isOtherValue(v));
                                            const next = txt.trim().length > 0 ? [...withoutOther, `Other: ${txt}`] : [...withoutOther, 'Other'];
                                            handleChange(next);
                                        }
                                        if (Array.isArray(value)) setShowOtherMulti((value as string[]).some(v => isOtherValue(v)));
                                        handleValidation(value);
                                    }}
                                    disabled={config?.uiMeta?.disabled}
                                />
                                {error && <ErrorText>{error}</ErrorText>}
                            </div>
                        )}
                    </div>
                );

            case FieldDataTypeEnum.SELECT:
                return (
                    <div>
                        <AMSSelect
                            staticOptions={selectOptions}
                            value={singleChoiceValue}
                            onChange={(selectedOption: any) => {
                                const option = Array.isArray(selectedOption)
                                    ? selectedOption[0]
                                    : selectedOption;
                                const nextValue = option?.value ? String(option.value) : '';
                                if (includeOther && nextValue === 'Other') {
                                    setShowOtherSingle(true);
                                    setOtherSingleText('');
                                    handleChange('Other');
                                } else {
                                    setShowOtherSingle(false);
                                    setOtherSingleText('');
                                    handleChange(nextValue);
                                }
                            }}
                            placeholder={config?.uiMeta?.placeholder || "Select option here"}
                            isDisabled={config?.uiMeta?.disabled}
                            isSearchable
                            defaultOptions
                        />
                        {includeOther && showOtherSingle && (
                            <div style={{ marginTop: 8 }}>
                                <SmallLabel theme={theme}>Please specify</SmallLabel>
                                <TextInput
                                    theme={theme}
                                    type="text"
                                    placeholder="Specify Other..."
                                    value={otherSingleText}
                                    onChange={(e) => setOtherSingleText(e.target.value)}
                                    onBlur={() => {
                                        const txt = otherSingleText;
                                        handleChange(txt.length > 0 ? `Other: ${txt}` : 'Other');
                                        setShowOtherSingle(includeOther && isOtherValue(value));
                                        handleValidation(value);
                                    }}
                                    disabled={config?.uiMeta?.disabled}
                                />
                                {error && <ErrorText>{error}</ErrorText>}
                            </div>
                        )}
                    </div>
                );

            case FieldDataTypeEnum.DATE:
                return (
                    <CustomInput
                        type="date"
                        {...getCommonProps()}
                        rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                    />
                );

            case FieldDataTypeEnum.TIME:
                return (
                    <CustomInput
                        type="time"
                        {...getCommonProps()}
                        rightIcon={<ClockIcon className="text-black" />}
                    />
                );

            case FieldDataTypeEnum.DATE_TIME:
                return (
                    <CustomInput
                        type={config?.uiMeta?.includeTimeInDate ? "datetime-local" : "date"}
                        {...getCommonProps()}
                        rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                    />
                );

            case FieldDataTypeEnum.MONTH_YEAR:
                return (
                    <CustomInput
                        type="month"
                        {...getCommonProps()}
                        rightIcon={<CalendarIcon color={theme.CorporateTheme.colors.textSecondary} />}
                    />
                );

            case FieldDataTypeEnum.FILE_UPLOAD:
                return (
                    <FileUpload
                        folder={uploadFolder}
                        onUploadSuccess={(key) => {
                            handleChange(key);
                        }}
                        onUploadError={(err) => {
                            showToastOutsideReact("Upload Error", err, "error");
                        }}
                        forPreview={forPreview}
                        isFileUpload={true}
                        bulkUpdatePreview={normalizedValue}
                    />
                );
            case FieldDataTypeEnum.IMAGE_UPLOAD:
                return (
                    <FileUpload
                        folder={uploadFolder}
                        onUploadSuccess={(key) => {
                            handleChange(key);
                        }}
                        onUploadError={(err) => {
                            showToastOutsideReact("Upload Error", err, "error");
                        }}
                        forPreview={forPreview}
                        bulkUpdatePreview={normalizedValue}
                    />
                );

            case FieldDataTypeEnum.DESCRIPTION:
                return (
                    <DescriptionContent
                        theme={theme}
                        dangerouslySetInnerHTML={{ __html: normalizeQuillAlignmentHtml(decodeHtmlEntities(config.uiMeta.descriptionContent || "")) }}
                    />
                );

            case FieldDataTypeEnum.SLIDER:
                return (
                    <SliderWrapper>
                        <SliderInput
                            type="range"
                            min={config?.uiMeta?.minValueSlider || 0}
                            max={config?.uiMeta?.maxValueSlider || 100}
                            value={value || config?.uiMeta?.minValueSlider || 0}
                            onChange={(e) => handleChange(Number(e.target.value))}
                            disabled={config?.uiMeta?.disabled}
                        />
                        <SliderValue>
                            {value || config?.uiMeta?.minValueSlider || 0} {config?.uiMeta?.unit || ""}
                        </SliderValue>
                    </SliderWrapper>
                );

            case FieldDataTypeEnum.VIDEO_EMBED:
            case FieldDataTypeEnum.AUDIO_EMBED:
                return (
                    <MediaPlayer
                        url={value}
                        onUrlChange={handleChange}
                        placeholder={config?.uiMeta?.placeholder || fieldDataType === FieldDataTypeEnum.AUDIO_EMBED ? "Enter audio URL" : "Enter video URL"}
                        disabled={config?.uiMeta?.disabled}
                    />
                );

            default:
                return <CustomInput {...getCommonProps()} />;
        }
    };

    return renderSwitch();
};

// Styled and helper components used only within field rendering
const SmallLabel = styled.span<{ theme: any }>(({ theme }) => ({
    fontSize: '12px',
    fontWeight: 500,
    display: 'inline-block',
    marginBottom: '4px',
    color: `${theme.CreateFormTheme.colors.headerColor}`
}));

const DescriptionContent = styled.div`
  padding: 12px;
  background-color: ${({ theme }) => theme.CorporateTheme.colors.formBuilderNormalBG};
  border-radius: 5px;
  border-left: 3px solid #1890ff;
  border-right: 3px solid #1890ff;
`;

const SliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SliderInput = styled.input`
  width: 100%;
`;

const SliderValue = styled.div`
  text-align: center;
  font-size: 14px;
  color: #666;
`;

const DisplayOnlyHeading = styled.div<{
    $align: string;
    $color: string;
    $fontSize: string;
    $fontWeight: string;
}>`
    width: 100%;
    padding: 8px 0 4px;
    line-height: 1.15;
        font-size: ${({ $fontSize }) => $fontSize};
        font-weight: ${({ $fontWeight }) => ($fontWeight === 'regular' ? 400 : $fontWeight === 'medium' ? 500 : 700)};
        color: ${({ $color }) => $color};
        text-align: ${({ $align }) => $align};
    word-break: break-word;
`;

const DisplayOnlyParagraph = styled.div<{
    $color: string;
    $fontSize: string;
    $fontWeight: string;
    $lineHeight: string;
    $preview?: boolean;
}>`
    width: 100%;
    padding: 4px 0;
    font-size: ${({ $fontSize }) => $fontSize};
    line-height: ${({ $lineHeight }) => $lineHeight};
    font-weight: ${({ $fontWeight }) => ($fontWeight === 'regular' ? 400 : $fontWeight === 'medium' ? 500 : 700)};
    color: ${({ $color }) => $color};
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: anywhere;

    &.ql-editor {
        padding-left: 0;
        padding-right: 0;
    }

    &.ql-editor p,
    &.ql-editor h1,
    &.ql-editor h2,
    &.ql-editor h3,
    &.ql-editor h4,
    &.ql-editor h5,
    &.ql-editor h6 {
        margin: 0;
    }

    a {
        pointer-events: ${({ $preview }) => ($preview ? 'none' : 'auto')};
    }
`;

const DisplayOnlyDivider = styled.div<{
    $color: string;
    $height: string;
    $style: string;
    $width: string;
    $spacing: string;
}>`
    width: ${({ $width }) => $width};
    height: 0;
    margin: ${({ $spacing }) => $spacing};
    border-top-width: ${({ $height }) => $height};
    border-top-style: ${({ $style }) => $style};
    border-top-color: ${({ $color }) => $color};
`;

interface MediaPlayerProps {
    url: string;
    onUrlChange: (url: string) => void;
    placeholder: string;
    disabled?: boolean;
}

const MediaPlayer: React.FC<MediaPlayerProps> = ({ url, onUrlChange, placeholder, disabled }) => {
    const { theme } = useTheme();
    const [mediaType, setMediaType] = useState<"audio" | "video" | "embed" | "unknown">("unknown");

    const getYouTubeVideoId = (url: string) => {
        const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[7].length === 11) ? match[7] : null;
    };

    const getVimeoVideoId = (url: string) => {
        const regExp = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)([0-9]+)/;
        const match = url.match(regExp);
        return match ? match[1] : null;
    };

    useEffect(() => {
        if (!url) { setMediaType("unknown"); return; }
        if (url.includes("youtube.com") || url.includes("youtu.be")) { setMediaType("embed"); return; }
        if (url.includes("vimeo.com")) { setMediaType("embed"); return; }
        const audioExtensions = [".mp3", ".wav", ".ogg", ".m4a", ".aac"];
        const videoExtensions = [".mp4", ".webm", ".mov", ".avi", ".mkv", ".flv", ".m3u8"];
        const extension = url.substring(url.lastIndexOf(".")).toLowerCase();
        if (audioExtensions.includes(extension)) setMediaType("audio");
        else if (videoExtensions.includes(extension)) setMediaType("video");
        else setMediaType("unknown");
    }, [url]);

    const renderEmbeddedPlayer = () => {
        const youTubeId = getYouTubeVideoId(url);
        const vimeoId = getVimeoVideoId(url);
        if (youTubeId) {
            return (
                <iframe
                    width="100%"
                    height="315"
                    src={`https://www.youtube.com/embed/${youTubeId}`}
                    title="YouTube video player"
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            );
        }
        if (vimeoId) {
            return (
                <iframe
                    src={`https://player.vimeo.com/video/${vimeoId}`}
                    width="100%"
                    height="315"
                    frameBorder={0}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                />
            );
        }
        return (
            <UnsupportedMessage>
                Unsupported embedded URL. Please provide a YouTube or Vimeo link, or a direct link to an audio/video file.
            </UnsupportedMessage>
        );
    };

    return (
        <div>
            <TextInput
                theme={theme}
                type="url"
                placeholder={placeholder}
                value={url}
                onChange={(e) => onUrlChange(e.target.value)}
                disabled={disabled}
            />
            {url && mediaType === "audio" && (
                <AudioPlayerWrapper>
                    <audio controls src={url} style={{ width: "100%" }}>
                        Your browser does not support the audio element.
                    </audio>
                </AudioPlayerWrapper>
            )}
            {url && mediaType === "video" && (
                <VideoPlayerWrapper>
                    <video controls src={url} style={{ width: "100%", maxHeight: "400px" }}>
                        Your browser does not support the video tag.
                    </video>
                </VideoPlayerWrapper>
            )}
            {url && mediaType === "embed" && <EmbedWrapper>{renderEmbeddedPlayer()}</EmbedWrapper>}
            {url && mediaType === "unknown" && (
                <UnsupportedMessage>
                    Unsupported media type. Please provide:
                    <br />• YouTube or Vimeo links
                    <br />• Direct links to audio files (MP3, WAV, OGG, M4A, AAC)
                    <br />• Direct links to video files (MP4, WebM, MOV, AVI, MKV)
                </UnsupportedMessage>
            )}
        </div>
    );
};

// Google Places Autocomplete
interface AddressAutocompleteProps {
    value: string | { formattedAddress?: string };
    onChange: (value: any) => void;
    onAutoFill?: (payload: {
        formattedAddress: string;
        value: string;
        components: Record<string, string>;
        location?: { lat: number; lng: number };
    }) => void;
    placeholder?: string;
    disabled?: boolean;
    mode?: 'address-line' | 'city' | 'state' | 'postal-code';
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
    value,
    onChange,
    onAutoFill,
    placeholder,
    disabled,
    mode = 'address-line',
}) => {
    const { theme } = useTheme();
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const autocompleteRef = React.useRef<any>(null);

    useEffect(() => {
        let cancelled = false;
        loadGoogleMapsScript()
            .then(() => {
                if (cancelled) return;
                const google: any = (window as any).google;
                if (!google?.maps?.places || !inputRef.current) return;
                const options = { fields: ["address_components", "formatted_address", "geometry", "place_id", "name"] };
                autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
                autocompleteRef.current.addListener("place_changed", () => {
                    const place = autocompleteRef.current.getPlace();
                    const formatted = place?.formatted_address || inputRef.current?.value || "";
                    const components: Record<string, string> = {};
                    (place?.address_components || []).forEach((c: any) => {
                        const type = c.types?.[0];
                        if (type) components[type] = c.long_name;
                    });
                    let derived: any = formatted;
                    if (mode === 'city') derived = components.locality || components.sublocality || components.administrative_area_level_2 || '';
                    else if (mode === 'state') derived = components.administrative_area_level_1 || '';
                    else if (mode === 'postal-code') derived = components.postal_code || '';
                    else if (mode === 'address-line') {
                        const street = [components.street_number, components.route].filter(Boolean).join(' ');
                        derived = street || formatted;
                    }
                    const payload = {
                        formattedAddress: formatted,
                        placeId: place?.place_id,
                        location: place?.geometry?.location ? { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() } : undefined,
                        components,
                        value: derived,
                    };
                    onChange(derived || formatted || '');
                    if (onAutoFill) {
                        onAutoFill({
                            formattedAddress: payload.formattedAddress,
                            value: payload.value,
                            components: payload.components,
                            location: payload.location,
                        });
                    }
                });
            })
            .catch(() => { });
        return () => { cancelled = true; };
    }, []);

    const displayValue = typeof value === "string" ? value : (value as any)?.value || (value as any)?.formattedAddress || "";

    return (
        <TextInput
            theme={theme}
            type="text"
            placeholder={placeholder || "Enter address"}
            value={displayValue}
            ref={inputRef as any}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            disabled={disabled}
        />
    );
};

const EmbedWrapper = styled.div`
  margin-top: 12px;
  width: 100%;
  iframe { border-radius: 5px; background-color: #000; }
`;

const AudioPlayerWrapper = styled.div`
  margin-top: 12px;
  width: 100%;
`;

const VideoPlayerWrapper = styled.div`
  margin-top: 12px;
  width: 100%;
  video { border-radius: 5px; background-color: #000; }
`;

const UnsupportedMessage = styled.div`
  margin-top: 8px;
  padding: 8px;
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  color: #ad6800;
  line-height: 1.4;
`;

const TextInput = styled(FormControl) <{ theme: any }>`
  padding: 10px 12px;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.CreateFormTheme?.colors?.inputBorders};
  background-color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputBackground};
  color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputColor};
  width: 100%;
  &::placeholder { color: ${({ theme }) => theme.CreateFormTheme?.colors?.placeHolder}; }
  &:disabled {
    background-color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputBackground};
    cursor: not-allowed;
    color: ${({ theme }) => theme.CreateFormTheme?.colors?.placeHolder};
  }
  &:focus {
    background-color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputBackground};
    color: ${({ theme }) => theme.CreateFormTheme?.colors?.inputColor};
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    outline: none;
  }
`;

export default FieldControl;
